'use client';
import cn from 'classnames';
import styles from './ProductGallery.module.scss';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { useState } from 'react';
import { getBackendImage } from '@/utils/getBackendImage';
import { Tag } from '@/atoms/Tag';
import { Mousewheel, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/mousewheel';
import { ProductDetailedType } from '@/types';
import { ModalGallery } from '@/atoms/ModalGallery';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { LikeButton } from '@/atoms/LikeButton';
import { useSelector } from 'react-redux';
import { selectFavourites } from '@/store';
import { useControlFavourites } from '@/hooks/useControlFavourites';

type ProductGalleryProps = {
  className?: string;
  product: ProductDetailedType;
};

type GalleryArrowProps = {
  onClick: () => void;
  bottom?: boolean;
};

export function ProductGallery({ className, product }: ProductGalleryProps) {
  const desktopClassName = cn(styles.desktop, className);
  const tabletClassName = cn(styles.tablet, className);
  const mobileClassName = cn(styles.mobile, className);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [showModalGallery, setShowModalGallery] = useState(false);
  const favourites = useSelector(selectFavourites);
  const isLiked = favourites.includes(product.id);
  const { addToFavourites, removeFromFavourites } = useControlFavourites();
  useBodyScrollLock(showModalGallery);

  const handleLikeClick = () => {
    if (isLiked) {
      removeFromFavourites(product.id);
    } else {
      addToFavourites({
        id: product.id,
        name: product.name,
        url: product.url,
        picture: product.pictures?.[0] ?? '',
        brand: product.brand?.name ?? '',
        price: product.price,
        oldPrice: product.oldPrice,
      });
    }
  };

  const renderCardsContent = (className?: string) => {
    return (
      <>
        {product.pictures.map((image, index) => (
          <SwiperSlide key={index} className={className}>
            <div className={styles.cardWrapper}>
              <div className={styles.card}>
                <img
                  src={getBackendImage(image)}
                  alt={image}
                  className={styles.cardImage}
                  onClick={() => setShowModalGallery(true)}
                />
                <LikeButton
                  onClick={handleLikeClick}
                  active={isLiked}
                  className={styles.likeButton}
                  isSmall
                />
                <div>{tags}</div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </>
    );
  };

  const adaptiveSliderConfig = {
    pagination: {
      clickable: true,
      bulletClass: styles.bullet,
      bulletActiveClass: styles.bulletActive,
      horizontalClass: styles.pagination,
    },
    className: styles.swiper,
    centeredSlides: true,
    modules: [Pagination],
  };

  const tags = (
    <div className={styles.tags}>
      {product.isNew && <Tag tooltip="Новинка" isNew />}
      {product.isTrend && <Tag tooltip="Популярный товар" isTrend />}
      {product.isExclusive && <Tag tooltip="Эксклюзив" isExclusive />}
      {product.isLimitedEdition && (
        <Tag tooltip="Лимитированная коллекция" isLimitedEdition />
      )}
      {product.isSale && <Tag tooltip="Распродажа" isSale />}
      {product.isPromo && <Tag tooltip="Акция" isPromo />}
      {!!product.discountPercent && product.discountPercent > 0 && (
        <Tag discount={product.discountPercent} />
      )}
    </div>
  );

  return (
    <>
      <div className={desktopClassName}>
        <div className={styles.column}>
          {product.pictures.length > 4 && (
            <GalleryArrow onClick={() => swiper?.slidePrev()} />
          )}
          <Swiper
            effect="slide"
            slidesPerView="auto"
            spaceBetween={12}
            direction="vertical"
            onSwiper={(swiper: SwiperClass) => {
              setSwiper(swiper);
            }}
            className={styles.swiper}
            modules={[Mousewheel]}
            mousewheel={{
              forceToAxis: true,
            }}
          >
            {product.pictures.map((image, index) => (
              <SwiperSlide key={index} className="custom-slide">
                <img
                  src={getBackendImage(image)}
                  alt={image}
                  className={cn(styles.preview, {
                    [styles.active]: activeImageIndex === index,
                  })}
                  onClick={() => setActiveImageIndex(index)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          {product.pictures.length > 4 && (
            <GalleryArrow bottom onClick={() => swiper?.slideNext()} />
          )}
        </div>
        <div className={styles.imageContainer}>
          <img
            src={getBackendImage(product.pictures[activeImageIndex])}
            alt="preview"
            className={styles.image}
            onClick={() => setShowModalGallery(true)}
          />
          {tags}
        </div>
      </div>
      <div className={tabletClassName}>
        <Swiper
          effect="slide"
          slidesPerView="auto"
          loop
          {...adaptiveSliderConfig}
        >
          {renderCardsContent('custom-slide')}
        </Swiper>
      </div>
      <div className={mobileClassName}>
        <Swiper effect="slide" slidesPerView={1} {...adaptiveSliderConfig}>
          {renderCardsContent()}
        </Swiper>
      </div>
      <ModalGallery
        active={showModalGallery}
        onClose={() => setShowModalGallery(false)}
        items={product.pictures}
      />
    </>
  );
}

const GalleryArrow = ({ onClick, bottom }: GalleryArrowProps) => {
  const arrowClassName = cn(styles.arrow, {
    [styles.bottom]: bottom,
  });

  return (
    <div className={arrowClassName} onClick={onClick}>
      <img
        src="/svg/chevron-black.svg"
        alt="arrow"
        className={styles.arrowIcon}
      />
    </div>
  );
};
